// Owner-directed 520 correction (Sep 14 2026) — bounded read-retry helper.
//
// Extracted from AdminDashboard.jsx solely so an isolated Jest test can
// exercise the retry semantics without pulling in React/react-router-dom.
// Behavior:
//   - Retries: 3 attempts (initial + 2 retries) with delays 2s, 4s.
//   - Only retries: ERR_NETWORK, ECONNABORTED, ERR_BAD_RESPONSE, HTTP 5xx
//     (including Cloudflare 520 / 521 / 522 / 524).
//   - Does NOT retry: 4xx (auth/permission/validation are real errors).
//   - On give-up, rethrows the FINAL error verbatim so callers can
//     surface the truthful status + detail to the admin toast.
//   - `signal` (AbortSignal) short-circuits BOTH the sleep between
//     retries AND the retry loop itself. Callers pass the same signal
//     to axios so the transport is cancelled and the retry loop does
//     not burn its sleep after the scope changes / component unmounts.
//
// Total worst-case wait: ~6s before surfacing an error.

export class RetryAbortedError extends Error {
  constructor(reason) {
    super(reason || "aborted");
    this.name = "RetryAbortedError";
    this.aborted = true;
  }
}

export const _isTransient = (err) => {
  if (!err) return false;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED" || err.code === "ERR_BAD_RESPONSE") return true;
  const s = err.response?.status;
  return typeof s === "number" && s >= 500 && s <= 599;
};

// `_sleep(ms, signal)` rejects immediately if the signal is aborted, or
// as soon as it becomes aborted while sleeping. Otherwise resolves after
// `ms`. Clears the timer + listener on both paths so nothing leaks.
export const _sleep = (ms, signal) => new Promise((resolve, reject) => {
  if (signal && signal.aborted) {
    reject(new RetryAbortedError(signal.reason?.message || "aborted"));
    return;
  }
  const timer = setTimeout(() => {
    if (signal) signal.removeEventListener("abort", onAbort);
    resolve();
  }, ms);
  const onAbort = () => {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onAbort);
    reject(new RetryAbortedError(signal?.reason?.message || "aborted"));
  };
  if (signal) signal.addEventListener("abort", onAbort, { once: true });
});

export const withRetry = async (fn, { tries = 3, delays = [2000, 4000], signal } = {}) => {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    if (signal && signal.aborted) throw new RetryAbortedError(signal.reason?.message || "aborted");
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      // Abort signal short-circuits ALL retries. The transport-level
      // axios cancel surfaces as ERR_CANCELED (or CanceledError); we
      // MUST NOT retry an aborted request.
      if (signal && signal.aborted) throw new RetryAbortedError(signal.reason?.message || "aborted");
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") throw err;
      if (i === tries - 1 || !_isTransient(err)) throw err;
      // eslint-disable-next-line no-console
      console.warn(`[withRetry] attempt ${i + 1}/${tries} failed, retrying in ${delays[i]}ms…`, err.message);
      try {
        await _sleep(delays[i], signal);
      } catch (sleepErr) {
        // Signal aborted during the sleep — surface as an abort, not the
        // underlying transient error, so callers can distinguish.
        throw sleepErr;
      }
    }
  }
  throw lastErr;
};
